import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Icon from "./Icon";
import { mySubscription } from "../api/subscriptionService";
import { useAuth } from "../contexts/AuthContext";
import { formatDate } from "../utils/formatters";

/**
 * Firm-wide renewal/suspension notice mounted in AppLayout. Quiet for
 * healthy subscriptions; warns during grace and when suspended (the API is
 * already hard-blocking by then). Customers see it too — their portal runs
 * on the firm's subscription.
 */
export default function SubscriptionBanner() {
  const { isAuthenticated, role } = useAuth();
  const { pathname } = useLocation();
  const [sub, setSub] = useState(null);

  // Re-fetch on every route change: the banner lives in the persistent
  // AppLayout, so mount-only fetching would go stale mid-session when the
  // nightly sweep flips a firm to grace/suspended.
  useEffect(() => {
    if (!isAuthenticated || role === "super_admin") return;
    let mounted = true;
    (async () => {
      try {
        const res = await mySubscription();
        if (mounted) setSub(res?.data);
      } catch {
        /* silent — banner is best-effort */
      }
    })();
    return () => { mounted = false; };
  }, [isAuthenticated, role, pathname]);

  if (!sub) return null;

  if (sub.firm?.status === "suspended") {
    return (
      <div className="banner banner-danger" style={bannerStyle("var(--danger, #b91c1c)")}>
        <Icon name="alert-triangle" size={14} />
        This firm's Smart CA subscription has expired and access is blocked. Please contact Devsoleil to renew.
      </div>
    );
  }

  if (sub.subscription?.status === "grace") {
    return (
      <div className="banner banner-warning" style={bannerStyle("var(--warning, #b45309)")}>
        <Icon name="clock" size={14} />
        Subscription payment overdue — service continues until {formatDate(sub.subscription.grace_until)}. Please arrange the renewal.
      </div>
    );
  }

  if (sub.subscription?.is_trial && sub.subscription?.status === "active") {
    return (
      <div className="banner" style={bannerStyle("var(--purple, #7c3aed)")}>
        <Icon name="clock" size={14} />
        You're on a free trial — ends {formatDate(sub.subscription.period_end)}. Contact Devsoleil to continue without interruption.
      </div>
    );
  }

  return null;
}

const bannerStyle = (color) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "8px 16px",
  fontSize: 13,
  fontWeight: 500,
  color: "#fff",
  background: color,
});
